import type { DisplayType, PortalAnnouncement } from "./types";

/** Mirror the kiosk's takeover rule: if any active announcement is a
 *  ``fullscreen_takeover``, only the takeover rows are shown (an
 *  evacuation suppresses concurrent info/maintenance). Otherwise the
 *  full list passes through. */
export function applyTakeoverSuppression(list: PortalAnnouncement[]): PortalAnnouncement[] {
  const takeover = list.filter((a) => a.display_type === "fullscreen_takeover");
  return takeover.length > 0 ? takeover : list;
}

/** Bucket announcements by display type for slot-based rendering. */
export function groupByDisplayType(list: PortalAnnouncement[]): Record<DisplayType, PortalAnnouncement[]> {
  const groups: Record<DisplayType, PortalAnnouncement[]> = {
    fullscreen: [],
    fullscreen_takeover: [],
    banner: [],
    corner: [],
  };
  for (const a of list) groups[a.display_type]?.push(a);
  return groups;
}

/** Whether an announcement may be *permanently* dismissed (persisted so
 *  it never re-shows). Critical / emergency / takeover announcements can
 *  be closed for the current view but must re-appear — you can't let
 *  someone permanently silence an evacuation alert. */
export function isPermanentlyDismissable(a: PortalAnnouncement): boolean {
  if (a.severity === "critical") return false;
  if (a.display_type === "fullscreen_takeover") return false;
  if (a.category.startsWith("emergency.")) return false;
  return true;
}

/** The page an announcement's "Open" button goes to, or ``null`` when it has
 *  none (blank) — the banner and corner card only render the button then. */
export function announcementOpenPath(announcement: Pick<PortalAnnouncement, "portal_path">): string | null {
  const path = announcement.portal_path?.trim();
  // Only a page of the app itself: the server validates it too, but a link the
  // host navigates to is never trusted blindly (no "//host", "/\\host", schemes).
  if (!path || !path.startsWith("/") || path.startsWith("//") || path.startsWith("/\\")) return null;
  return path;
}
